<script>
    import { fade } from 'svelte/transition';
    import { push } from 'svelte-spa-router';
    import { onMount } from 'svelte';
    import Header from '../components/Header.svelte';
    import TopInfo from '../components/TopInfo.svelte';
    import Preloader from '../components/Preloader.svelte';
    import UserStore from '../stores/user';
    import fetchData from '../api.js';

    export let params;
    let isLoading = false;
    let sendError = '';
    let sendSuccess = '';
    let apiResponse = '';
    let uploadError = '';
    let uploadedFileName = '';
    let orderNumber = '';
    let isEditMode = false;
    let isLockedByPurchaseOrderId = false;
    let purchaseOrderId = '';
    let editOrderId = '';
    let editOrderStatus = '';
    let editOrderCreatedDate = '';
    let editOrderCreatedTime = '';
    const PURCHASE_ORDER_CREATE_ACTION = 'crm_create_purchase_order';
    const PURCHASE_ORDER_GET_ACTION = 'crm_get_purchase_order';
    const PURCHASE_ORDER_UPDATE_ACTION = 'crm_update_purchase_order';
    const PURCHASE_ITEMS_ACTION = 'purchase_items';
    const MAX_SKU_SUGGESTIONS = 8;

    const createEmptyItem = () => ({
        sku: '',
        barcode: '',
        description_product: '',
        quantity: 1
    });

    let orderItems = [createEmptyItem()];
    let purchaseItemsCatalog = [];
    let activeSuggestionRowIndex = -1;

    onMount(() => {
        if (!$UserStore || !$UserStore.data) {
            push('/login');
            return;
        }

        fetchPurchaseItemsCatalog();
        hydrateFromRoute();
    });

    $: if ($UserStore?.data && params?.id && String(params.id) !== String(editOrderId)) {
        hydrateFromRoute();
    }

    async function hydrateFromRoute() {
        const routeId = Number(params?.id);

        if (!Number.isInteger(routeId) || routeId <= 0) {
            isEditMode = false;
            isLockedByPurchaseOrderId = false;
            purchaseOrderId = '';
            editOrderId = '';
            editOrderStatus = '';
            editOrderCreatedDate = '';
            editOrderCreatedTime = '';
            orderNumber = '';
            orderItems = [createEmptyItem()];
            return;
        }

        isEditMode = true;
        editOrderId = String(routeId);
        await fetchExistingOrderById(editOrderId);
    }

    async function fetchExistingOrderById(orderId) {
        isLoading = true;
        sendError = '';

        try {
            const response = await fetchData({
                action: PURCHASE_ORDER_GET_ACTION,
                token: $UserStore.data,
                id: Number(orderId),
                order_id: Number(orderId)
            });

            if (!response || response.error || response.success === 0) {
                sendError = response?.msg || response?.error || 'טעינת פרטי ההזמנה נכשלה';
                return;
            }

            const rawOrder = Array.isArray(response.data)
                ? response.data[0] || {}
                : response.data || {};

            const rawItems = Array.isArray(rawOrder.order_items)
                ? rawOrder.order_items
                : Array.isArray(response.data)
                    ? (response.data[0]?.order_items || [])
                    : [];

            editOrderId = String(rawOrder.id ?? editOrderId);
            orderNumber = (rawOrder.order_number ?? orderNumber).toString();
            purchaseOrderId = (rawOrder.purchase_order_id ?? '').toString();
            editOrderStatus = (rawOrder.status ?? '').toString();

            const createdRaw = rawOrder.created_at || rawOrder.created || rawOrder.date || '';
            const createdParts = String(createdRaw).split(' ');
            editOrderCreatedDate = createdParts[0] || editOrderCreatedDate;
            editOrderCreatedTime = createdParts[1] || editOrderCreatedTime;

            isLockedByPurchaseOrderId = purchaseOrderId !== '';
            orderItems = rawItems.length
                ? rawItems.map((item) => normalizeItem(item))
                : [createEmptyItem()];
        } catch (error) {
            sendError = error.message || 'אירעה שגיאה בזמן טעינת פרטי ההזמנה';
        } finally {
            isLoading = false;
        }
    }

    function normalizeCatalogItem(item, index) {
        return {
            id: item.id || `catalog-${index}`,
            sku: (item.sku || item.product_sku || item.makat || item.sku_code || '').toString().trim(),
            barcode: (item.barcode || item.ean || item.upc || '').toString().trim(),
            description_product: (item.description_product || item.name || item.product_name || item.title || '').toString().trim()
        };
    }

    async function fetchPurchaseItemsCatalog() {
        try {
            const response = await fetchData({
                action: PURCHASE_ITEMS_ACTION,
                token: $UserStore.data,
                limit: 500,
                is_active: 1
            });

            if (!response || response.error || response.success === 0) {
                purchaseItemsCatalog = [];
                return;
            }

            const rawItems = Array.isArray(response.data)
                ? response.data
                : Array.isArray(response.items)
                    ? response.items
                    : [];

            purchaseItemsCatalog = rawItems
                .map((item, index) => normalizeCatalogItem(item, index))
                .filter((item) => item.sku !== '');
        } catch (error) {
            purchaseItemsCatalog = [];
        }
    }

    function getSkuSuggestions(searchValue) {
        const term = String(searchValue || '').trim().toLowerCase();
        if (!term || !purchaseItemsCatalog.length) {
            return [];
        }

        const ranked = purchaseItemsCatalog
            .filter((item) => {
                return item.sku.toLowerCase().includes(term) || item.description_product.toLowerCase().includes(term);
            })
            .sort((a, b) => {
                const aStarts = a.sku.toLowerCase().startsWith(term) ? 0 : 1;
                const bStarts = b.sku.toLowerCase().startsWith(term) ? 0 : 1;
                if (aStarts !== bStarts) {
                    return aStarts - bStarts;
                }

                return a.sku.localeCompare(b.sku);
            });

        return ranked.slice(0, MAX_SKU_SUGGESTIONS);
    }

    function applySuggestionToRow(index, suggestion) {
        if (!suggestion) {
            return;
        }

        orderItems = orderItems.map((item, rowIndex) => {
            if (rowIndex !== index) {
                return item;
            }

            return {
                ...item,
                sku: suggestion.sku || item.sku,
                barcode: suggestion.barcode || item.barcode,
                description_product: suggestion.description_product || item.description_product
            };
        });

        activeSuggestionRowIndex = -1;
    }

    function handleSkuInput(index, value) {
        activeSuggestionRowIndex = index;

        const exactMatch = purchaseItemsCatalog.find((item) => {
            return item.sku.toLowerCase() === String(value || '').trim().toLowerCase();
        });

        if (exactMatch) {
            applySuggestionToRow(index, exactMatch);
            activeSuggestionRowIndex = index;
        }
    }

    function handleSkuBlur(index) {
        setTimeout(() => {
            if (activeSuggestionRowIndex === index) {
                activeSuggestionRowIndex = -1;
            }
        }, 120);
    }

    function addItem() {
        if (isEditMode && isLockedByPurchaseOrderId) {
            return;
        }

        orderItems = [...orderItems, createEmptyItem()];
    }

    function removeItem(index) {
        if (isEditMode && isLockedByPurchaseOrderId) {
            return;
        }

        if (orderItems.length === 1) {
            orderItems = [createEmptyItem()];
            return;
        }

        orderItems = orderItems.filter((_, itemIndex) => itemIndex !== index);
    }

    function normalizeItem(item) {
        const quantityNumber = Number(item.quantity);

        return {
            sku: (item.sku ?? '').toString().trim(),
            barcode: (item.barcode ?? '').toString().trim(),
            description_product: (item.description_product ?? '').toString().trim(),
            quantity: Number.isFinite(quantityNumber) ? quantityNumber : 0
        };
    }

    function buildPayload() {
        const normalizedItems = orderItems.map(normalizeItem);

        const validItems = normalizedItems.filter((item) => {
            return item.sku !== '' || item.barcode !== '' || item.description_product !== '';
        });

        const payload = {
            order_number: orderNumber.toString().trim(),
            order_items: validItems
        };

        if (isEditMode && editOrderId) {
            payload.id = Number(editOrderId);
        }

        return payload;
    }

    function validatePayload(payload) {
        if (!payload.order_items.length) {
            return 'יש להוסיף לפחות מוצר אחד';
        }

        if (isEditMode && !editOrderId) {
            return 'לא נמצא מזהה פנימי להזמנה לעריכה';
        }

        for (const [index, item] of payload.order_items.entries()) {
            if (!item.sku) {
                return `שורת מוצר ${index + 1}: שדה SKU הוא חובה`;
            }

            if (!item.barcode) {
                return `שורת מוצר ${index + 1}: שדה ברקוד הוא חובה`;
            }

            if (!item.description_product) {
                return `שורת מוצר ${index + 1}: שדה תיאור מוצר הוא חובה`;
            }

            if (!Number.isInteger(item.quantity) || item.quantity <= 0) {
                return `שורת מוצר ${index + 1}: כמות חייבת להיות מספר שלם וחיובי`;
            }
        }

        return '';
    }

    function parseCsvContent(content) {
        const lines = content
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter((line) => line !== '');

        if (!lines.length) {
            throw new Error('הקובץ ריק');
        }

        const headers = lines[0].split(',').map((header) => header.trim().toLowerCase());

        const requiredHeaders = ['sku', 'barcode', 'description_product', 'quantity'];
        const missingHeaders = requiredHeaders.filter((required) => !headers.includes(required));

        if (missingHeaders.length) {
            throw new Error(`כותרות חסרות בקובץ CSV: ${missingHeaders.join(', ')}`);
        }

        const headerIndex = {
            sku: headers.indexOf('sku'),
            barcode: headers.indexOf('barcode'),
            description_product: headers.indexOf('description_product'),
            quantity: headers.indexOf('quantity')
        };

        const parsedItems = lines.slice(1).map((line) => {
            const cols = line.split(',').map((col) => col.trim());

            return {
                sku: cols[headerIndex.sku] ?? '',
                barcode: cols[headerIndex.barcode] ?? '',
                description_product: cols[headerIndex.description_product] ?? '',
                quantity: cols[headerIndex.quantity] ?? ''
            };
        });

        return parsedItems;
    }

    async function handleFileUpload(event) {
        if (isEditMode && isLockedByPurchaseOrderId) {
            return;
        }

        sendError = '';
        sendSuccess = '';
        uploadError = '';

        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        uploadedFileName = file.name;

        try {
            const fileText = await file.text();
            const lowerName = file.name.toLowerCase();
            let parsedItems = [];

            if (lowerName.endsWith('.json')) {
                const jsonData = JSON.parse(fileText);

                if (Array.isArray(jsonData)) {
                    parsedItems = jsonData;
                } else if (Array.isArray(jsonData.order_items)) {
                    parsedItems = jsonData.order_items;
                } else {
                    throw new Error('בקובץ JSON לא נמצא מערך order_items');
                }
            } else if (lowerName.endsWith('.csv')) {
                parsedItems = parseCsvContent(fileText);
            } else {
                throw new Error('פורמט קובץ לא נתמך. יש להעלות JSON או CSV');
            }

            orderItems = parsedItems.map((item) => normalizeItem(item));

            if (!orderItems.length) {
                orderItems = [createEmptyItem()];
                throw new Error('לא נמצאו מוצרים תקינים בקובץ');
            }
        } catch (error) {
            uploadError = error.message || 'שגיאה בקריאת הקובץ';
            orderItems = [createEmptyItem()];
        }
    }

    async function sendPurchaseOrder() {
        if (isEditMode && isLockedByPurchaseOrderId) {
            sendError = 'הזמנה זו נעולה לעריכה כי הוחזר purchase_order_id';
            return;
        }

        sendError = '';
        sendSuccess = '';
        apiResponse = '';

        const payload = buildPayload();
        const validationError = validatePayload(payload);

        if (validationError) {
            sendError = validationError;
            return;
        }

        isLoading = true;

        try {
            const action = isEditMode ? PURCHASE_ORDER_UPDATE_ACTION : PURCHASE_ORDER_CREATE_ACTION;
            const postData = {
                action,
                token: $UserStore.data,
                ...payload
            };

            const response = await fetchData(postData);
            apiResponse = JSON.stringify(response, null, 2);

            if (!response) {
                sendError = 'אירעה שגיאה בזמן שליחה לשרת';
                return;
            }

            if (response.error || response.success === 0) {
                sendError = response.msg || response.error || 'השליחה נכשלה';
                return;
            }

            sendSuccess = response.msg || 'הזמנת הרכש נשלחה בהצלחה';

            const returnedPurchaseOrderId = response?.purchase_order_id || response?.data?.purchase_order_id || '';
            if (returnedPurchaseOrderId) {
                purchaseOrderId = String(returnedPurchaseOrderId);
                isLockedByPurchaseOrderId = true;
            }
        } catch (error) {
            sendError = error.message || 'אירעה שגיאה בזמן שליחה לשרת';
        } finally {
            isLoading = false;
        }
    }

</script>

<div class="page-container" transition:fade={{ delay: 250, duration: 300 }}>
    <Preloader {isLoading} />
    <Header />

    <div class="page">
        <TopInfo />

        <div class="shipment purchase-order-page">
            <div class="header-row">
                <h1 class="text-[22px] font-bold">{isEditMode ? 'עריכת הזמנת רכש' : 'יצירת הזמנת רכש'}</h1>
                <div class="nav-actions">
                    <button type="button" class="create_button" on:click={() => push('/purchase-orders/items')}>פריטי רכש</button>
                    <button type="button" class="create_button" on:click={() => push('/purchase-orders/locations')}>מיקומי יציאה</button>
                    <button type="button" class="create_button" on:click={() => push('/purchase-orders/history')}>היסטוריית הזמנות רכש</button>
                </div>
            </div>
            <p class="subtitle">אפשר להוסיף מוצרים ידנית או להעלות קובץ JSON/CSV, ואז לשלוח לשרת באמצעות action ייעודי.</p>

            {#if isEditMode}
                <div class="card">
                    <div class="row-head">
                        <h2>פרטי הזמנה לעריכה</h2>
                    </div>
                    <div class="edit-meta-grid">
                        <div class="meta-item">
                            <div class="meta-label">מזהה פנימי</div>
                            <div class="meta-value">{editOrderId || '-'}</div>
                        </div>
                        <div class="meta-item">
                            <div class="meta-label">סטטוס</div>
                            <div class="meta-value">{editOrderStatus || '-'}</div>
                        </div>
                        <div class="meta-item">
                            <div class="meta-label">תאריך</div>
                            <div class="meta-value">{editOrderCreatedDate || '-'}</div>
                        </div>
                        <div class="meta-item">
                            <div class="meta-label">שעה</div>
                            <div class="meta-value">{editOrderCreatedTime || '-'}</div>
                        </div>
                    </div>
                </div>
            {/if}

            {#if isEditMode && isLockedByPurchaseOrderId}
                <div class="lock-note">הזמנה זו נעולה לעריכה כי הוחזר purchase_order_id: {purchaseOrderId}</div>
            {/if}

            <div class="card">
                <div class="row-head">
                    <h2>טעינת קובץ</h2>
                    <span class="file-name">{uploadedFileName}</span>
                </div>

                <input type="file" accept=".json,.csv,application/json,text/csv" on:change={handleFileUpload} disabled={isEditMode && isLockedByPurchaseOrderId} />

                <p class="helper">
                    CSV חייב לכלול כותרות: sku, barcode, description_product, quantity
                </p>

                {#if uploadError}
                    <div class="error">{uploadError}</div>
                {/if}
            </div>

            <div class="card">
                <label class="field-label" for="order-number-input">מספר הזמנה</label>
                <input id="order-number-input" type="text" class="field-input" bind:value={orderNumber} placeholder="לדוגמה: PO-2026-001" disabled={isEditMode} />
            </div>

            <div class="card">
                <div class="row-head">
                    <h2>מוצרים להזמנה</h2>
                    <button type="button" class="create_button" on:click={addItem} disabled={isEditMode && isLockedByPurchaseOrderId}>הוסף מוצר</button>
                </div>

                <div class="table-wrap">
                    <table class="items-table">
                        <thead>
                            <tr>
                                <th>SKU</th>
                                <th>ברקוד</th>
                                <th>תיאור מוצר</th>
                                <th>כמות</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each orderItems as item, index}
                                <tr>
                                    <td>
                                        <div class="sku-autocomplete">
                                            <input
                                                type="text"
                                                bind:value={item.sku}
                                                placeholder="SKU-550"
                                                autocomplete="off"
                                                disabled={isEditMode && isLockedByPurchaseOrderId}
                                                on:focus={() => (activeSuggestionRowIndex = index)}
                                                on:blur={() => handleSkuBlur(index)}
                                                on:input={(event) => handleSkuInput(index, event.target.value)}
                                            />

                                            {#if activeSuggestionRowIndex === index}
                                                {@const suggestions = getSkuSuggestions(item.sku)}
                                                {#if suggestions.length}
                                                    <div class="sku-suggestions">
                                                        {#each suggestions as suggestion}
                                                            <button
                                                                type="button"
                                                                class="sku-suggestion-item"
                                                                on:mousedown|preventDefault={() => applySuggestionToRow(index, suggestion)}
                                                            >
                                                                <span class="sku-main">{suggestion.sku}</span>
                                                                <span class="sku-sub">{suggestion.description_product || suggestion.barcode}</span>
                                                            </button>
                                                        {/each}
                                                    </div>
                                                {/if}
                                            {/if}
                                        </div>
                                    </td>
                                    <td>
                                        <input type="text" bind:value={item.barcode} placeholder="7290000123456" disabled={isEditMode && isLockedByPurchaseOrderId} />
                                    </td>
                                    <td>
                                        <input type="text" bind:value={item.description_product} placeholder="תיאור מוצר" disabled={isEditMode && isLockedByPurchaseOrderId} />
                                    </td>
                                    <td class="quantity-cell">
                                        <input type="number" min="1" step="1" bind:value={item.quantity} disabled={isEditMode && isLockedByPurchaseOrderId} />
                                    </td>
                                    <td class="remove-cell">
                                        <button type="button" class="remove-button" on:click={() => removeItem(index)} disabled={isEditMode && isLockedByPurchaseOrderId}>מחק</button>
                                    </td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="actions">
                <button type="button" class="create_button" on:click={sendPurchaseOrder} disabled={isEditMode && isLockedByPurchaseOrderId}>שלח הזמנת רכש</button>
            </div>

            {#if sendError}
                <div class="error">{sendError}</div>
            {/if}

            {#if sendSuccess}
                <div class="success">{sendSuccess}</div>
            {/if}

            {#if apiResponse}
                <div class="card">
                    <h2>תגובת API</h2>
                    <pre>{apiResponse}</pre>
                </div>
            {/if}
        </div>
    </div>
</div>

<style>
    .purchase-order-page {
        width: 100%;
        padding: 20px;
        box-sizing: border-box;
    }

    .subtitle {
        margin-top: 8px;
        color: #5b6475;
    }

    .lock-note {
        margin-top: 12px;
        border-radius: 10px;
        padding: 10px 12px;
        font-size: 14px;
        background: #fff7e9;
        color: #9e5d00;
        border: 1px solid #ffe1ad;
    }

    .header-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        flex-wrap: wrap;
    }

    .nav-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .card {
        background: #fff;
        border-radius: 14px;
        border: 1px solid #eceff5;
        box-shadow: 0 8px 24px rgba(17, 43, 85, 0.08);
        padding: 16px;
        margin-top: 16px;
    }

    .field-label {
        display: block;
        font-size: 13px;
        color: #4f5970;
        margin-bottom: 6px;
    }

    .field-input {
        width: 100%;
        min-height: 38px;
        border: 1px solid #d6deea;
        border-radius: 8px;
        padding: 0 10px;
        box-sizing: border-box;
    }

    .row-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 12px;
    }

    .row-head h2 {
        margin: 0;
        font-size: 18px;
        font-weight: 700;
    }

    .file-name {
        font-size: 13px;
        color: #667089;
    }

    .helper {
        margin: 8px 0 0;
        font-size: 13px;
        color: #667089;
    }

    .edit-meta-grid {
        display: grid;
        gap: 10px;
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }


    .meta-item {
        border: 1px solid #e1e8f3;
        border-radius: 10px;
        padding: 10px;
        background: #f8fbff;
    }

    .meta-label {
        color: #667089;
        font-size: 12px;
    }

    .meta-value {
        color: #223252;
        font-size: 14px;
        font-weight: 700;
        margin-top: 4px;
    }

    .table-wrap {
        width: 100%;
        overflow: visible;
        position: relative;
    }

    .items-table {
        width: 100%;
        border-collapse: collapse;
        min-width: 860px;
    }

    .items-table th,
    .items-table td {
        padding: 10px 8px;
        border-bottom: 1px solid #eef2f7;
        vertical-align: middle;
    }

    .items-table th {
        text-align: right;
        color: #3f4760;
        font-size: 13px;
        white-space: nowrap;
    }

    .items-table td input {
        width: 100%;
        min-height: 38px;
        border: 1px solid #d6deea;
        border-radius: 8px;
        padding: 0 10px;
        box-sizing: border-box;
    }

    .sku-autocomplete {
        position: relative;
    }

    .sku-suggestions {
        position: absolute;
        z-index: 5;
        right: 0;
        left: 0;
        top: calc(100% + 4px);
        background: #ffffff;
        border: 1px solid #dfe6f2;
        border-radius: 10px;
        box-shadow: 0 8px 20px rgba(17, 43, 85, 0.16);
        max-height: 220px;
        overflow-y: auto;
    }

    .sku-suggestion-item {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 3px;
        padding: 9px 10px;
        background: #fff;
        border: 0;
        border-bottom: 1px solid #edf2f9;
        cursor: pointer;
        text-align: right;
    }

    .sku-suggestion-item:last-child {
        border-bottom: 0;
    }

    .sku-suggestion-item:hover {
        background: #f7faff;
    }

    .sku-main {
        color: #263249;
        font-size: 13px;
        font-weight: 700;
    }

    .sku-sub {
        color: #65728c;
        font-size: 12px;
        line-height: 1.2;
    }

    .quantity-cell {
        width: 120px;
    }

    .remove-cell {
        width: 95px;
    }

    .remove-button {
        border: 1px solid #f5ccd3;
        background: #fff5f7;
        color: #be2f4f;
        min-height: 36px;
        border-radius: 8px;
        padding: 0 12px;
        cursor: pointer;
    }

    .actions {
        margin-top: 18px;
        display: flex;
        justify-content: flex-end;
    }

    pre {
        background: #f8f9fc;
        border: 1px solid #e8edf5;
        border-radius: 10px;
        padding: 12px;
        margin: 0;
        overflow: auto;
        direction: ltr;
        text-align: left;
        max-height: 360px;
        font-size: 13px;
    }

    .error,
    .success {
        margin-top: 12px;
        border-radius: 10px;
        padding: 10px 12px;
        font-size: 14px;
    }

    .error {
        background: #fff2f4;
        color: #b2203f;
        border: 1px solid #ffd6de;
    }

    .success {
        background: #edfff4;
        color: #1c8f4a;
        border: 1px solid #caefd9;
    }

    @media screen and (max-width: 800px) {
        .purchase-order-page {
            padding: 10px;
        }

        .row-head {
            flex-wrap: wrap;
        }

        .actions {
            justify-content: stretch;
        }

        .actions .create_button {
            width: 100%;
        }

        .edit-meta-grid {
            grid-template-columns: 1fr 1fr;
        }

    }
</style>
